import { TabPanel } from 'react-aria-components';
import { Route, Routes, useParams } from 'react-router';
import { Trans } from '@lingui/react/macro';

import { GenericError } from '@/components/GenericError/GenericError';
import Badge from '@/design_system/Badge';
import Stack from '@/design_system/Stack';
import Tabs from '@/design_system/Tabs';
import { Tab, TabList } from '@/design_system/Tabs/Tabs';
import Paragraph from '@/design_system/Typography/Paragraph';
import Title from '@/design_system/Typography/Title';
import { useEntity } from '@/models/entity';
import { ErrorState } from '@/routes/SettingsBeta/components/ErrorState';
import { SettingsBreadcrumbParent } from '@/routes/SettingsBeta/components/Layout/Layout';
import { Loading } from '@/routes/SettingsBeta/components/Loading';
import { NotFoundError } from '@/utils/error/NotFoundError';

import {
  AfterSalesTraitIcon,
  StockLocationTraitIcon,
  StoreTraitIcon,
  WorkshopTraitIcon,
} from '../components/TraitIcon';

import { AgreementsSection } from './sections/AgreementsSection';
import { GroupsSection } from './sections/GroupsSection';
import { MembersSection } from './sections/MembersSection';
import { OverviewSection } from './sections/OverviewSection';
import { PlaceSection } from './sections/PlaceSection';
import { StoreSection } from './sections/StoreSection';
import { WorkshopSection } from './sections/WorkshopSection';

export const Entity = () => {
  const { '*': section, id } = useParams();

  const {
    data: entity,
    isLoading: isEntityLoading,
    isError: isEntityError,
    error,
  } = useEntity(id!);

  if (isEntityLoading) {
    return <Loading />;
  }

  if (isEntityError || !entity) {
    let errorMessage = <GenericError />;

    if (error instanceof NotFoundError) {
      errorMessage = (
        <Paragraph>
          <Trans id="settings.entities.entities.entity.not-found">
            Unknown entity or you do not have access to it.
          </Trans>
        </Paragraph>
      );
    }

    return <ErrorState errorMessage={errorMessage} />;
  }

  const isStore = entity.entityOrganizations.some((eo) => eo.trait === 'store');
  const isAftersales = entity.entityOrganizations.some((eo) => eo.trait === 'aftersales');
  const isWorkshop = entity.entityOrganizations.some((eo) => eo.trait === 'workshop');
  const isStockLocation = entity.entityOrganizations.some((eo) => eo.trait === 'stockLocation');
  const isPlace = isStore || isWorkshop;
  const isExternal = entity.entityOrganizations.some((eo) => eo.external);

  return (
    <main>
      <Stack
        row
        className="settings-section__record-header"
        gap="0.5rem"
        alignItems="center"
        flexWrap="wrap"
      >
        <SettingsBreadcrumbParent
          to="/settings_beta/network/entities"
          label={<Trans id="settings.entities.entities.entity.back-to-entities">Entities</Trans>}
        />

        <Title as="h1" size="200" weight="medium">
          <Stack row gap="0.5rem" alignItems="center">
            <Stack row gap="0.25rem" className="text-secondary">
              {isStore && <StoreTraitIcon />}
              {isAftersales && <AfterSalesTraitIcon />}
              {isWorkshop && <WorkshopTraitIcon />}
              {isStockLocation && <StockLocationTraitIcon />}
            </Stack>
            {entity.name}
            {isExternal && (
              <Badge size="x-large" color="secondary" hasBorder>
                <Trans id="settings.organization.entities.entities.external">External</Trans>
              </Badge>
            )}
          </Stack>
        </Title>
      </Stack>

      <Tabs selectedKey={section || 'overview'}>
        <TabList>
          <Tab id="overview" href={`/settings_beta/network/entities/${entity.id}`}>
            <Trans id="settings.entities.entities.entity.tab.overview">Overview</Trans>
          </Tab>
          {isStore && (
            <Tab id="store" href={`/settings_beta/network/entities/${entity.id}/store`}>
              <Trans id="settings.entities.entities.entity.tab.store">Store</Trans>
            </Tab>
          )}
          {isWorkshop && (
            <Tab id="workshop" href={`/settings_beta/network/entities/${entity.id}/workshop`}>
              <Trans id="settings.entities.entities.entity.tab.workshop">Workshop</Trans>
            </Tab>
          )}
          {isPlace && (
            <Tab id="place" href={`/settings_beta/network/entities/${entity.id}/place`}>
              <Trans id="settings.entities.entities.entity.tab.place">Location</Trans>
            </Tab>
          )}
          <Tab id="members" href={`/settings_beta/network/entities/${entity.id}/members`}>
            <Trans id="settings.entities.entities.entity.tab.members">Members</Trans>
          </Tab>
          <Tab id="groups" href={`/settings_beta/network/entities/${entity.id}/groups`}>
            <Trans id="settings.entities.entities.entity.tab.groups">Groups</Trans>
          </Tab>
          <Tab id="agreements" href={`/settings_beta/network/entities/${entity.id}/agreements`}>
            <Trans id="settings.entities.entities.entity.tab.agreements">Agreements</Trans>
          </Tab>
        </TabList>

        <TabPanel id={section || 'overview'}>
          <Routes>
            <Route path="" element={<OverviewSection entity={entity} />} />
            {isStore && <Route path="store" element={<StoreSection entity={entity} />} />}
            {isWorkshop && <Route path="workshop" element={<WorkshopSection entity={entity} />} />}
            {isPlace && <Route path="place" element={<PlaceSection entity={entity} />} />}
            <Route path="members" element={<MembersSection entity={entity} />} />
            <Route path="groups" element={<GroupsSection entity={entity} />} />
            <Route path="agreements" element={<AgreementsSection entity={entity} />} />
          </Routes>
        </TabPanel>
      </Tabs>
    </main>
  );
};
